"use client";

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../../lib/utils';

export interface TeamMember {
  id: string;
  name: string;
  role: string;
  image: string;
  bio?: string;
  expertise?: string[];
  stats?: { [key: string]: string | number };
}

export interface TeamCarouselProps {
  /** Array of team members */
  members: TeamMember[];
  /** Title displayed above the carousel */
  title?: string;
  /** Title font size */
  titleSize?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  /** Title color */
  titleColor?: string;
  /** Background color or gradient */
  background?: string;
  /** Card width in pixels */
  cardWidth?: number;
  /** Card height in pixels */
  cardHeight?: number;
  /** Card border radius */
  cardRadius?: number;
  /** Enable/disable navigation arrows */
  showArrows?: boolean;
  /** Enable/disable dots indicator */
  showDots?: boolean;
  /** Enable/disable keyboard navigation */
  keyboardNavigation?: boolean;
  /** Enable/disable touch/swipe navigation */
  touchNavigation?: boolean;
  /** Animation duration in milliseconds */
  animationDuration?: number;
  /** Auto-play interval in milliseconds (0 to disable) */
  autoPlay?: number;
  /** Pause auto-play on hover */
  pauseOnHover?: boolean;
  /** Number of visible cards on each side */
  visibleCards?: number;
  /** Scale factor for side cards */
  sideCardScale?: number;
  /** Opacity for side cards */
  sideCardOpacity?: number;
  /** Apply grayscale filter to side cards */
  grayscaleEffect?: boolean;
  /** Custom className for container */
  className?: string;
  /** Custom className for cards */
  cardClassName?: string;
  /** Custom className for title */
  titleClassName?: string;
  /** Member info position */
  infoPosition?: 'bottom' | 'overlay' | 'none';
  /** Info text color */
  infoTextColor?: string;
  /** Info background */
  infoBackground?: string;
  /** Callback when active member changes */
  onMemberChange?: (member: TeamMember, index: number) => void;
  /** Callback when card is clicked */
  onCardClick?: (member: TeamMember, index: number) => void;
  /** Initial active index */
  initialIndex?: number;
}

export const TeamCarousel: React.FC<TeamCarouselProps> = ({
  members,
  title = "OUR TEAM",
  titleSize = "2xl",
  titleColor = "rgba(0, 76, 255, 1)",
  background,
  cardWidth = 320,
  cardHeight = 420,
  cardRadius = 24,
  showArrows = true,
  showDots = true,
  keyboardNavigation = true,
  touchNavigation = true,
  animationDuration = 500, // Reduced for smoother animation
  autoPlay = 0,
  pauseOnHover = true,
  visibleCards = 3, // Increased for better visibility
  sideCardScale = 0.85,
  sideCardOpacity = 0.6,
  grayscaleEffect = true,
  className,
  cardClassName,
  titleClassName,
  infoPosition = "bottom",
  infoTextColor = "#FFFFFF",
  infoBackground = "rgba(15, 23, 42, 0.8)",
  onMemberChange,
  onCardClick,
  initialIndex = 0,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [direction, setDirection] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const autoPlayRef = useRef<NodeJS.Timeout | null>(null);

  const totalMembers = members.length;

  const paginate = useCallback(
    (newDirection: number) => {
      if (totalMembers === 0) return;
      setDirection(newDirection);
      const nextIndex = (currentIndex + newDirection + totalMembers) % totalMembers;
      setCurrentIndex(nextIndex);
      onMemberChange?.(members[nextIndex], nextIndex);
    },
    [currentIndex, totalMembers, members, onMemberChange]
  );

  // Calculate position with better logic for all cards
  const calculatePosition = (index: number): string => {
    const diff = (index - currentIndex + totalMembers) % totalMembers;
    
    // Handle wrap-around for continuous carousel
    let adjustedDiff = diff;
    if (diff > totalMembers / 2) {
      adjustedDiff = diff - totalMembers;
    } else if (diff < -totalMembers / 2) {
      adjustedDiff = diff + totalMembers;
    }

    // Always show all cards, just position them based on their relative position
    if (adjustedDiff === 0) return 'center';
    if (adjustedDiff === 1) return 'right-1';
    if (adjustedDiff === 2) return 'right-2';
    if (adjustedDiff === -1) return 'left-1';
    if (adjustedDiff === -2) return 'left-2';
    if (adjustedDiff === 3) return 'right-3';
    if (adjustedDiff === -3) return 'left-3';
    
    // For cards further away, position them off-screen
    if (adjustedDiff > 0) return 'right-hidden';
    return 'left-hidden';
  };

  const getVariantStyles = (position: string) => {
    const transition = {
      duration: animationDuration / 1000,
      ease: "easeInOut", // Simpler easing for smoother animation
    };

    const baseCardOffset = cardWidth * 1.2; // Increased spacing

    switch (position) {
      case 'center':
        return {
          zIndex: 30,
          opacity: 1,
          scale: 1,
          x: 0,
          filter: 'grayscale(0%) brightness(1)',
          pointerEvents: 'auto',
          transition: { ...transition, scale: { duration: 0.3 } },
        };
      case 'right-1':
        return {
          zIndex: 20,
          opacity: sideCardOpacity,
          scale: sideCardScale,
          x: baseCardOffset,
          filter: grayscaleEffect ? 'grayscale(50%) brightness(0.8)' : 'grayscale(0%)',
          pointerEvents: 'auto',
          transition,
        };
      case 'right-2':
        return {
          zIndex: 10,
          opacity: sideCardOpacity * 0.5,
          scale: sideCardScale * 0.8,
          x: baseCardOffset * 2,
          filter: grayscaleEffect ? 'grayscale(80%) brightness(0.6)' : 'grayscale(0%)',
          pointerEvents: 'auto',
          transition,
        };
      case 'right-3':
        return {
          zIndex: 5,
          opacity: sideCardOpacity * 0.3,
          scale: sideCardScale * 0.7,
          x: baseCardOffset * 3,
          filter: grayscaleEffect ? 'grayscale(100%) brightness(0.4)' : 'grayscale(0%)',
          pointerEvents: 'none',
          transition,
        };
      case 'left-1':
        return {
          zIndex: 20,
          opacity: sideCardOpacity,
          scale: sideCardScale,
          x: -baseCardOffset,
          filter: grayscaleEffect ? 'grayscale(50%) brightness(0.8)' : 'grayscale(0%)',
          pointerEvents: 'auto',
          transition,
        };
      case 'left-2':
        return {
          zIndex: 10,
          opacity: sideCardOpacity * 0.5,
          scale: sideCardScale * 0.8,
          x: -baseCardOffset * 2,
          filter: grayscaleEffect ? 'grayscale(80%) brightness(0.6)' : 'grayscale(0%)',
          pointerEvents: 'auto',
          transition,
        };
      case 'left-3':
        return {
          zIndex: 5,
          opacity: sideCardOpacity * 0.3,
          scale: sideCardScale * 0.7,
          x: -baseCardOffset * 3,
          filter: grayscaleEffect ? 'grayscale(100%) brightness(0.4)' : 'grayscale(0%)',
          pointerEvents: 'none',
          transition,
        };
      default:
        return {
          zIndex: 0,
          opacity: 0,
          scale: 0.6,
          x: position.includes('right') ? baseCardOffset * 4 : -baseCardOffset * 4,
          pointerEvents: 'none',
          filter: 'grayscale(100%) brightness(0.3)',
          transition,
        };
    }
  };

  // Improved auto-play with requestAnimationFrame for smoother timing
  useEffect(() => {
    if (autoPlayRef.current) {
      clearTimeout(autoPlayRef.current);
    }

    if (autoPlay > 0 && !isHovering && totalMembers > 1) {
      autoPlayRef.current = setTimeout(() => {
        paginate(1);
      }, autoPlay);
    }

    return () => {
      if (autoPlayRef.current) {
        clearTimeout(autoPlayRef.current);
      }
    };
  }, [autoPlay, isHovering, paginate, totalMembers, currentIndex]);

  // Keyboard navigation
  useEffect(() => {
    if (!keyboardNavigation) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        paginate(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        paginate(1);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [keyboardNavigation, paginate]);

  // Touch navigation with better sensitivity
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!touchNavigation) return;
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchNavigation) return;
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchNavigation) return;

    const swipeThreshold = 30; // Reduced threshold for better sensitivity
    const diff = touchStart - touchEnd;

    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        paginate(1); // Swipe left = next
      } else {
        paginate(-1); // Swipe right = previous
      }
    }
    
    setTouchStart(0);
    setTouchEnd(0);
  };

  const titleSizeClasses = {
    sm: 'text-4xl',
    md: 'text-5xl',
    lg: 'text-6xl',
    xl: 'text-7xl',
    '2xl': 'text-8xl',
  };

  return (
    <div
      id="team-carousel-container"
      className={cn(`
        min-h-screen flex flex-col items-center justify-center 
        overflow-hidden relative w-full
      `, className)}
      style={{ background: background }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseEnter={() => pauseOnHover && setIsHovering(true)}
      onMouseLeave={() => pauseOnHover && setIsHovering(false)}
    >
      {/* Title */}
      {title && (
        <h2
          className={cn(
            "text-white uppercase tracking-tight absolute top-12 left-1/2 transform -translate-x-1/2 pointer-events-none whitespace-nowrap",
            titleSizeClasses[titleSize],
            titleClassName
          )}
          style={{
            color: 'transparent',
            background: `linear-gradient(to bottom, ${titleColor}75 40%, transparent 76%)`,
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
          }}
        >
          {title}
        </h2>
      )}

      {/* Carousel Container */}
      <div
        className="w-full max-w-7xl relative  "
        style={{
          height: cardHeight + 100,
          perspective: '1000px',
        }}
      >
        {/* Navigation Arrows - Fixed positioning */}
        {showArrows && totalMembers > 1 && (
          <div className='w-full bg-green-500'>
            <motion.button
              onClick={() => paginate(-1)}
              className="absolute -left-16 top-1/2 transform -translate-y-1/2 
                backdrop-blur-xl bg-white/10 border border-white/20 
                text-white w-14 h-14 rounded-full flex items-center justify-center 
                z-40 transition-all duration-300 hover:bg-white/20 hover:scale-110 
                hover:border-white/30 shadow-[0_0_40px_rgba(108,99,255,0.3)]"
              whileTap={{ scale: 0.9 }}
            >
              <ChevronLeft className="w-6 h-6" />
            </motion.button>
            
            <motion.button
              onClick={() => paginate(1)}
              className="absolute -right-16 top-1/2 transform -translate-y-1/2 
                backdrop-blur-xl bg-white/10 border border-white/20 
                text-white w-14 h-14 rounded-full flex items-center justify-center 
                z-40 transition-all duration-300 hover:bg-white/20 hover:scale-110 
                hover:border-white/30 shadow-[0_0_40px_rgba(108,99,255,0.3)]"
              whileTap={{ scale: 0.9 }}
            >
              <ChevronRight className="w-6 h-6" />
            </motion.button>
          </div>
        )}

        {/* Cards Track */}
        <div
          className="w-full h-full flex justify-center items-center relative"
          style={{ transformStyle: 'preserve-3d' }}
        >
          <AnimatePresence initial={false} custom={direction}>
            {members.map((member, index) => {
              const position = calculatePosition(index);
              const isCurrent = index === currentIndex;

              return (
                <motion.div
                  key={member.id}
                  className={cn(
                    "absolute overflow-hidden cursor-pointer",
                    "backdrop-blur-xl bg-white/5 border border-white/15",
                    cardClassName
                  )}
                  style={{
                    width: cardWidth,
                    height: cardHeight,
                    borderRadius: cardRadius,
                    top: '50%',
                    left: '50%',
                    marginLeft: -cardWidth / 2,
                    marginTop: -cardHeight / 2,
                  }}
                  initial={getVariantStyles('hidden')}
                  animate={getVariantStyles(position)}
                  exit={getVariantStyles('hidden')}
                  whileHover={isCurrent ? { scale: 1.05 } : {}}
                  onClick={() => {
                    if (!isCurrent) {
                      const newDirection = index > currentIndex ? 1 : -1;
                      setDirection(newDirection);
                      setCurrentIndex(index);
                      onMemberChange?.(members[index], index);
                    }
                    onCardClick?.(member, index);
                  }}
                >
                  {/* Platinum border effect */}
                  <div className="absolute -inset-px bg-gradient-to-br from-white/10 to-transparent rounded-2xl" />
                  
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-3/5 object-cover"
                  />
                  
                  {/* Member Info Overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
                    <h3 className="text-xl font-bold text-white mb-1">{member.name}</h3>
                    <p className="text-sm text-[#6C63FF] font-medium">{member.role}</p>
                    {member.expertise && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {member.expertise.slice(0, 2).map((skill, i) => (
                          <span
                            key={i}
                            className="px-2 py-1 text-xs bg-[#6C63FF]/20 text-[#6C63FF] rounded"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </div>

      {/* Detailed Info Section */}
      {infoPosition === 'bottom' && members[currentIndex] && (
        <motion.div
          key={members[currentIndex].id + "-info"}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className=" max-w-3xl mx-auto px-6"
        >
          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-8">
            <div className="flex items-start gap-6">
              <div>
                <h3 className="text-3xl font-bold bg-gradient-to-r from-white to-white/90 bg-clip-text text-transparent mb-2">
                  {members[currentIndex].name}
                </h3>
                <p className="text-lg text-[#6C63FF] font-medium mb-4">
                  {members[currentIndex].role}
                </p>
                <p className="text-gray-300 leading-relaxed mb-6">
                  {members[currentIndex].bio}
                </p>
                
                {/* Expertise */}
                {members[currentIndex].expertise && (
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-400 mb-3 uppercase tracking-wider">
                      Expertise
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {members[currentIndex].expertise!.map((skill, i) => (
                        <span
                          key={i}
                          className="px-3 py-1.5 text-sm bg-white/5 border border-white/10 rounded-lg text-gray-300"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Stats */}
                {members[currentIndex].stats && (
                  <div className="flex gap-8">
                    {Object.entries(members[currentIndex].stats!).map(([key, value], i) => (
                      <div key={key} className="text-center">
                        <div className="text-2xl font-bold text-white">{value}</div>
                        <div className="text-sm text-gray-400 capitalize">{key}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Dots Indicator */}
      {showDots && totalMembers > 1 && (
        <div className="flex justify-center gap-2 mt-12">
          {members.map((_, index) => (
            <motion.button
              key={index}
              onClick={() => {
                if (index !== currentIndex) {
                  const newDirection = index > currentIndex ? 1 : -1;
                  setDirection(newDirection);
                  setCurrentIndex(index);
                  onMemberChange?.(members[index], index);
                }
              }}
              className="relative"
              whileTap={{ scale: 0.9 }}
            >
              <div
                className={cn(
                  "w-3 h-3 rounded-full transition-all duration-300",
                  index === currentIndex
                    ? "bg-[#6C63FF] scale-125"
                    : "bg-white/20 hover:bg-white/40"
                )}
              />
              {index === currentIndex && (
                <motion.div
                  className="absolute -inset-2 bg-[#6C63FF]/20 rounded-full blur-sm"
                  layoutId="activeDot"
                />
              )}
            </motion.button>
          ))}
        </div>
      )}
    </div>
  );
};

export default TeamCarousel;